import React, { useMemo, useState } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import {
  AlertCircle,
  MessageSquarePlus,
  MessagesSquare,
  Plug,
  Settings,
  Sparkles,
  Star,
  User,
  Wrench,
} from "lucide-react";
import { mutate } from "swr";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Switch } from "~/components/ui/switch";
import useToast from "~/components/ui/toast";
import useNextGenHeader from "~/contexts/nextgen-header";
import useUser from "~/contexts/user";
import { useFetcher } from "~/hooks/useFetcher";
import { buildEntityBreadcrumbs, getSectionSegment } from "~/pages/manifold-nextgen/breadcrumbs";
import { SectionNavLink } from "~/pages/manifold-nextgen/components/SectionNavLink";
import { SectionSidebar } from "~/pages/manifold-nextgen/components/SectionSidebar";
import { ShareButton } from "~/pages/manifold-nextgen/components/share/ShareButton";
import { ShareModal } from "~/pages/manifold-nextgen/components/share/ShareModal";
import { useAgentShare } from "~/pages/manifold-nextgen/hooks/useAgentShare";
import { useCanDeleteAgent } from "~/pages/manifold-nextgen/hooks/useCanDeleteAgent";
import { useCanDuplicateAgent } from "~/pages/manifold-nextgen/hooks/useCanDuplicateAgent";
import { useCanEditAgent } from "~/pages/manifold-nextgen/hooks/useCanEditAgent";
import { useCanManageAIAgents } from "~/pages/manifold-nextgen/hooks/useCanManageAIAgents";
import { useCanShareAgent } from "~/pages/manifold-nextgen/hooks/useCanShareAgent";
import { ROUTE_NEXTGEN_VIEW } from "~/router/constants";
import {
  Agent,
  AgentScope,
  AgentStatus,
  agentListPath,
  agentPath,
  updateAgent,
} from "~/services/agents";

// The Cancer Data Scientist is the SYSTEM agent backing the Dataset Assistant
// (app_id DATASET_ASSISTANT). Disabling it would hide the platform's flagship
// assistant from every user, so its status toggle stays locked even for the
// managers who can otherwise flip SYSTEM agents. Matched by stable name + scope,
// not display_name, so CUSTOMER copies remain freely toggleable.
const DATASET_ASSISTANT_AGENT_NAME = "dataset-assistant";

const NAV_ITEMS = [
  { label: "Profile", path: "profile", icon: <User className="size-4" /> },
  { label: "Skills", path: "skills", icon: <Sparkles className="size-4" /> },
  { label: "Toolkits", path: "toolkits", icon: <Wrench className="size-4" /> },
  { label: "Connectors", path: "connectors", icon: <Plug className="size-4" /> },
  {
    label: "Featured Sessions",
    path: "featured-conversations",
    icon: <Star className="size-4" />,
  },
  {
    label: "Starter Prompts",
    path: "starter-prompts",
    icon: <MessageSquarePlus className="size-4" />,
  },
  { label: "Sessions", path: "sessions", icon: <MessagesSquare className="size-4" /> },
] as const;

// Section path → breadcrumb label, derived from the nav so the two never drift.
// "profile" is the agent root (already shown as the name crumb) and is excluded.
// "settings" is rendered separately (it is permission-gated, not part of
// NAV_ITEMS) so its label is added explicitly.
const SECTION_LABELS: Record<string, string> = {
  ...Object.fromEntries(
    NAV_ITEMS.filter((item) => item.path !== "profile").map((item) => [item.path, item.label])
  ),
  settings: "Settings",
};

export const AgentDetail = () => {
  const { agentId } = useParams<{ agentId: string }>();
  const location = useLocation();
  const { data: agent, error, isLoading } = useFetcher<Agent>(agentPath(agentId!));
  const canManage = useCanManageAIAgents();
  const canEdit = useCanEditAgent(agent);
  const canDelete = useCanDeleteAgent(agent);
  const currentUser = useUser();
  const { showError } = useToast();

  const [enableConfirmOpen, setEnableConfirmOpen] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // Editors toggle status on CUSTOMER agents (status is part of the edit grant);
  // SYSTEM agents stay manage:agents-only, the one residual use of that role.
  const canToggleStatus = canEdit || (!!agent && agent.scope === AgentScope.SYSTEM && canManage);

  // The flagship SYSTEM dataset-assistant is shown with its status toggle but
  // locked, so it can never be hidden from users.
  const isStatusLocked =
    !!agent && agent.scope === AgentScope.SYSTEM && agent.name === DATASET_ASSISTANT_AGENT_NAME;

  // Per-principal sharing is a CUSTOMER-agent feature; SYSTEM agents skip the
  // Share action entirely (the Share modal below is CUSTOMER-gated). Whether the
  // caller may share resolves from the manager-only can_share grant — see
  // useCanShareAgent.
  const isCustomer = !!agent && agent.scope === AgentScope.CUSTOMER;
  const canShare = useCanShareAgent(agent);
  const canDuplicate = useCanDuplicateAgent(agent);

  // Prefetch grants for anyone who can share, so the list is loaded before the
  // modal opens. Sharing a role change is a client-side revoke-then-grant keyed
  // on the current role; a modal opened against a still-loading list would skip
  // the revoke and leave a principal holding two role tuples.
  const {
    collaborators,
    isLoading: collaboratorsLoading,
    collaboratorsError,
    handleShare,
    accessLevels,
    defaultAccessLevel,
  } = useAgentShare(agentId!, canShare);

  const sectionSegment = getSectionSegment(location.pathname, agentId);
  const sectionLabel = sectionSegment ? SECTION_LABELS[sectionSegment] : undefined;

  const headerBreadcrumbs = useMemo(
    () =>
      buildEntityBreadcrumbs({
        rootHref: `${ROUTE_NEXTGEN_VIEW}/agents`,
        entityName: agent?.display_name,
        // Link the name back to the agent root only when a deeper section is
        // showing, so it stays a plain label on the profile itself.
        entityHref: sectionLabel && agentId ? `${ROUTE_NEXTGEN_VIEW}/agents/${agentId}` : undefined,
        sectionLabel,
      }),
    [agent?.display_name, sectionLabel, agentId]
  );

  const headerActions = useMemo<[React.ReactNode?]>(() => {
    // Duplicate / delete moved to the Settings page reached from the sidebar.
    const shareNode = canShare ? (
      <ShareButton key="share-agent" label="Share Agent" onClick={() => setShareOpen(true)} />
    ) : undefined;

    return [shareNode];
  }, [canShare]);

  useNextGenHeader({ breadcrumbs: headerBreadcrumbs, actions: headerActions });

  const doToggle = async (status: AgentStatus) => {
    setIsToggling(true);
    try {
      await updateAgent(agentId!, { status });
      await Promise.all([mutate(agentPath(agentId!)), mutate(agentListPath())]);
    } catch {
      showError("Failed to update agent status");
    } finally {
      setIsToggling(false);
      setEnableConfirmOpen(false);
    }
  };

  const handleToggleStatus = (checked: boolean) => {
    if (checked) {
      setEnableConfirmOpen(true);
    } else {
      doToggle(AgentStatus.DISABLED);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-full">
        <div className="w-48 shrink-0 border-r border-border px-4 py-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="mx-1 my-1 h-8 animate-pulse rounded-md bg-muted" />
          ))}
        </div>
        <div className="flex-1 p-6">
          <div className="h-64 animate-pulse rounded-lg bg-muted" />
        </div>
      </div>
    );
  }

  if (error || !agent) {
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 py-16 text-center text-destructive"
      >
        <AlertCircle className="size-8" aria-hidden="true" />
        <p className="text-sm font-medium">Failed to load agent</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-full overflow-hidden">
        <SectionSidebar ariaLabel="Agent sections">
          {canToggleStatus && (
            <div className="flex flex-col gap-1 border-b border-border px-4 py-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">Enable</span>
                <Switch
                  checked={agent.status === AgentStatus.ENABLED}
                  onCheckedChange={handleToggleStatus}
                  disabled={isToggling || isStatusLocked}
                  aria-label="Enable agent"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {agent.status === AgentStatus.ENABLED
                  ? "This agent is visible to users"
                  : "This agent is hidden from users"}
              </p>
            </div>
          )}

          <div className="flex flex-col gap-1 px-4 py-3">
            {NAV_ITEMS.map(({ label, path, icon }) => (
              <SectionNavLink key={path} to={path} end icon={icon} label={label} />
            ))}
            {(canDuplicate || canDelete) && (
              <SectionNavLink
                to="settings"
                end
                icon={<Settings className="size-4" />}
                label="Settings"
              />
            )}
          </div>
        </SectionSidebar>

        <div className="flex-1 overflow-y-auto p-6">
          <Outlet context={agent} />
        </div>
      </div>

      <AlertDialog open={enableConfirmOpen} onOpenChange={setEnableConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Enable &ldquo;{agent.display_name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              Once enabled, this agent becomes visible to users, can be added to projects, and used
              in conversations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isToggling}>Cancel</AlertDialogCancel>
            <Button onClick={() => doToggle(AgentStatus.ENABLED)} disabled={isToggling}>
              {isToggling ? "Enabling…" : "Enable"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isCustomer && (
        <ShareModal
          open={shareOpen}
          onOpenChange={setShareOpen}
          resourceLabel="agent"
          resourceName={agent.display_name}
          collaborators={collaborators}
          collaboratorsLoading={collaboratorsLoading}
          collaboratorsError={collaboratorsError}
          accessLevels={accessLevels}
          defaultAccessLevel={defaultAccessLevel}
          onShare={handleShare}
          currentUserEmail={currentUser?.email}
        />
      )}
    </>
  );
};
